import React, { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { NetworkStatus } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';

import { GQLParty } from '@jewelutions/core-crm-server-api-client-domain';

import { userLanguage } from '../../../../core/AuthContext';
import { JwlTable, LoadingSpinner, ErrorComponent, Chip } from '../../../../components';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import AccessoryCreateDialog from '../AccessoryCreateDialog';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';

interface IProps {
  party: GQLParty;
}

const AccessoriesList: React.FC<IProps> = ({ party }: IProps) => {
  const history = useHistory();
  const [errorComponentState, setErrorComponentState] = useState(false);
  const { t: translate } = useTranslation();
  const [isCreateAccessoryDialogOpen, setIsCreateAccessoryDialogOpen] = useState(false);

  const {
    data: accessoriesData,
    loading: accessoriesLoading,
    error: accessoriesError,
    refetch: refetchAccessories,
    networkStatus: accessoriesNetworkStatus
  } = GQLOperationBuilder.accessory.getAccessoriesByPlatform();

  if (accessoriesLoading || accessoriesNetworkStatus === NetworkStatus.refetch) return <LoadingSpinner />;

  if (accessoriesError && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={accessoriesError?.stack}
        callback={refetchAccessories}
        message={accessoriesError.message}
      />
    );

  const columns = ['Title', 'Description', 'Created Date', 'Publishable'];

  const getAccessoriesRows = () => {
    return GQLOperationBuilder.accessory.accessoryAssetArrayFromResponse(accessoriesData).map(accessory => {
      return {
        id: accessory.id,
        cells: [
          accessory.accessory.title[userLanguage],
          accessory.accessory.description[userLanguage],
          new Date(Date.parse(accessory.createdAt)).toLocaleString(),
          <Chip
            size="small"
            mr={1}
            mb={1}
            label={accessory.isPublishable ? 'YES' : 'NO'}
            $active={accessory.isPublishable}
          />
        ]
      };
    });
  };

  const navigateToAccessoryDetails = (accessoryId: string) => {
    history.push(PlatformNavigation.accessoryDetails.pathFromParams({ accessoryId: accessoryId }));
  };

  return (
    <Fragment>
      <JwlTable
        title="Platform accessories list"
        description={`The list of all accessories defined in the system.`}
        columns={columns}
        rows={getAccessoriesRows()}
        onTableRowClick={navigateToAccessoryDetails}
        children={
          <Button
            variant="contained"
            color={'primary'}
            onClick={() => setIsCreateAccessoryDialogOpen(true)}
            style={{ marginLeft: '4px' }}
          >
            <AddIcon />
            <span style={{ marginLeft: '4px' }}>Add Accessory</span>
          </Button>
        }
      />
      {isCreateAccessoryDialogOpen && (
        <AccessoryCreateDialog
          party={party}
          onClose={success => {
            if (success === true) {
              successNotification(toast, 'Accessory created successfully', () => {});
            }

            setIsCreateAccessoryDialogOpen(false);
            refetchAccessories();
          }}
        />
      )}
    </Fragment>
  );
};

export default AccessoriesList;
